import React, { FC, useEffect, useState } from "react"

import { connectBreadcrumb } from "react-instantsearch-dom"
import { useIntl } from "react-intl"

import BreadcrumbSEO from "~/components/Breadcrumb/BreadcrumbSEO"
import Link from "~/components/Link/Link"
import { Body } from "~/components/ui"
import { capitalizeFirstLetter } from "~/utils/string"

import css from "./CustomBreadcrumb.module.scss"

type BreadcrumbProvided = {
  items: any[]
  refine: (value?: string) => any
  createURL: (value?: string) => any
}

type propType = BreadcrumbProvided & {
  location: Location
  baseItems: any
  ingredient?: string
}

const Breadcrumb: FC<propType> = props => {
  const urlParams = new URLSearchParams(props.location.search)
  const queryURLParam = urlParams.get("q")

  if (
    !props.items.length &&
    (!props.baseItems?.items || props.baseItems.items.length <= 1)
  ) {
    return null
  }

  return queryURLParam ? (
    <ResearchBreadcrumb queryURLParam={queryURLParam} />
  ) : (
    <BasicBreadcrumb {...props} />
  )
}

type ResearchPropType = {
  queryURLParam: string
}
const ResearchBreadcrumb = ({ queryURLParam }: ResearchPropType) => {
  const intl = useIntl()

  return (
    <div className={css.breadcrumb}>
      <Link to="search">
        <Body variant="body1" color="gray1">
          {intl.formatMessage({
            id: "category/text:recipes",
          })}
        </Body>
      </Link>
      <>
        <Body variant="body1" color="gray1">
          /
        </Body>
        <Link to="search" queryParams={{ q: queryURLParam }}>
          <Body variant="body1" color="gray1">
            {queryURLParam}
          </Body>
        </Link>
      </>
    </div>
  )
}

const convertItemsFromHierarchicalMenuToBreadcrumb = (items: any) => {
  const returnItems: any = []
  if (items) {
    items
      .filter((item: any) => item.isRefined)
      .forEach(function (item: any) {
        returnItems.push(item)
        const childItems = convertItemsFromHierarchicalMenuToBreadcrumb(
          item.items
        )
        childItems != undefined && returnItems.push(...childItems)
      })
  }
  return returnItems.length ? returnItems : null
}

const BasicBreadcrumb = ({
  items,
  createURL,
  baseItems,
  ingredient,
}: propType) => {
  const intl = useIntl()
  const itemsBreadcrumbSEO = !baseItems
    ? []
    : convertItemsFromHierarchicalMenuToBreadcrumb(baseItems.items)?.map(
        (item: any) => {
          return {
            label: item.label,
            link: `/${intl.locale}${createURL(item.value).replace(
              `/${ingredient}/`,
              "/"
            )}`,
          }
        }
      ) || []
  itemsBreadcrumbSEO.unshift({
    label: intl.formatMessage({
      id: "category/text:recipes",
    }),
    link: `/${intl.locale}${createURL()}`,
  })
  if (ingredient) {
    itemsBreadcrumbSEO.push({
      label: capitalizeFirstLetter(ingredient),
      link: `${itemsBreadcrumbSEO.slice(-1)[0].link}${ingredient}/`,
    })
  }
  const [displayedItems, setDisplayedItems] = useState<any[]>(
    convertItemsFromHierarchicalMenuToBreadcrumb(baseItems?.items) ??
      items ??
      []
  )

  useEffect(() => {
    setDisplayedItems(items)
  }, [items])

  return displayedItems.length > 0 ? (
    <div className={css.breadcrumb}>
      <BreadcrumbSEO items={itemsBreadcrumbSEO} />
      <Link to={`/${intl.locale}${createURL()}`} absoluteLink>
        <Body variant="body1" color="gray1">
          {intl.formatMessage({
            id: "category/text:recipes",
          })}
        </Body>
      </Link>
      {displayedItems.map((item: any) => (
        <div className={css.nextCategory} key={item.value}>
          <Body variant="body1" color="gray1">
            /
          </Body>
          <Link
            to={`/${intl.locale}${createURL(item.value).replace(
              `/${ingredient}/`,
              "/"
            )}`}
            absoluteLink
          >
            <Body variant="body1" color="gray1">
              {item.label}
            </Body>
          </Link>
        </div>
      ))}
      {ingredient ? (
        <div className={css.nextCategory}>
          <Body variant="body1" color="gray1">
            /
          </Body>
          <Body variant="body1" color="gray1">
            {capitalizeFirstLetter(ingredient)}
          </Body>
        </div>
      ) : null}
    </div>
  ) : null
}

const CustomBreadcrumb = connectBreadcrumb(Breadcrumb)
export default CustomBreadcrumb
